import { useEffect, useState } from "react";
import { IControlAttributes, IPageLink, ITableLookup } from "../../../base/models";
import { useAuth } from "../../../../modules/auth";
import { useGetProperties } from "../../../base/hooks";
import { useLazyGetBookingsByContactIdQuery } from "./properties/shuttleDetailsApi";
import { ControlType, FetchStatus, IEntity, Property } from "../../../base/types";
import { Registry } from "../../../base/register";
import { ShuttleBookingProperties } from "./properties";
import moment from "moment";
import { Spinner } from "../../../widgets";
import { useDataContext } from "../../page/context";
import { PageLinkHelper } from "../../../base/helpers";
import { useNavigate } from "react-router-dom";
import { IFieldValue } from "../../../services/models";
import { useUpdateMutation } from "../../../services/data/tableDataApi";
import { BookingsDetails } from "./BookingsDetails";

export const ShuttlePaymentSuccessfulComponent = (props: IControlAttributes) => {
  const { control } = props;
  const { properties } = useGetProperties({ bindings: control.bindings });
  const navigateToOnCancel: IPageLink | undefined = properties[Property.CancelPageLink];
  const successPage: IPageLink | undefined = properties[Property.SuccessPage];
  const failedPage: IPageLink | undefined = properties[Property.FailedPage];
  const bookingStatus: number | undefined = properties[Property.BookingStatus];
  const latestRecord: boolean | undefined = properties[Property.LatestRecord];
  const enablePayment: boolean | undefined = properties[Property.PaymentEnable];
  const paymentDoneToggle: boolean | undefined = properties[Property.PaymentDone];
  const enableRefetch: boolean | undefined = properties[Property.EnableRefetch];
  const enableTimer: boolean | undefined = properties[Property.EnableTimer];
  const tableLookup: ITableLookup = properties[Property.TableLookup] || {};
  const { setData } = useDataContext();
  const { user, siteId } = useAuth();
  const [loading, setLoading] = useState<string | null>(null);
  const navigate = useNavigate();
  const [updateRecord] = useUpdateMutation();
  const [getBookings, { data: bookings, isLoading }] = useLazyGetBookingsByContactIdQuery();
  const [fetchCount, setFetchCount] = useState(0);

  useEffect(() => {
    if (!user?.contactId) return; // Ensure required data is present

    // Fetch only once if enableRefetch is false
    if (!enableRefetch) {
      fetchBookings();
      return;
    }

    fetchBookings();
    const intervalId = setInterval(() => {
      if (fetchCount <= 5) {
        fetchBookings();
        setFetchCount((prev) => prev + 1);
      } else {
        clearInterval(intervalId);
      }
    }, 5000);

    return () => clearInterval(intervalId);
    // eslint-disable-next-line
  }, [user, bookingStatus, fetchCount, enableRefetch]);

  const fetchBookings = () => {
    if (!user?.contactId) return;
    getBookings({
      contactId: user?.contactId,
    });
  };

  const handleChangeStatus = (booking: string) => {
    const record: IEntity = {
      new_bookingstatus: 100000004,
    };

    setLoading(booking);
    saveFormData(record, booking)
      .then(data => {
        setData({ type: "Card", result: data, status: FetchStatus.Success });
        if (siteId && navigateToOnCancel && navigateToOnCancel.pageId) {
          navigate(PageLinkHelper.getPageLinkUrl(siteId, navigateToOnCancel));
        }
        fetchBookings();
      })
      .catch(err => {
        const message = err.data.message || 'An error occurred while processing your request. Please try again later.';
        console.error(message);
      })
      .finally(() => setLoading(null));
  };

  const saveFormData = (record: IEntity, booking: string): Promise<IEntity> => {
    const fields: IFieldValue[] = Object.entries(record)
      .filter(([_, value]) => value !== undefined)
      .map(([field, value]) => ({ field, value, validate: true }));

    return updateRecord({ portalId: siteId || '', userId: user?.contactId, tableId: "edtec365_booking", sysId: booking, data: fields, triggers: true })
      .unwrap()
      .catch(err => {
        const message = err.data.message || 'An error occurred while processing your request. Please try again later.';
        console.error(message);
        return Promise.reject(message);
      });
  };

  const sortedBookings = bookings?.slice().sort((a, b) => {
    const dateA = moment(a.createdon).utcOffset("+05:30");
    const dateB = moment(b.createdon).utcOffset("+05:30");
    return dateA.isBefore(dateB) ? 1 : -1;
  });

  const displayBookings = latestRecord
    ? sortedBookings?.slice(0, 1)
    : sortedBookings;
  return (
    <>
      {isLoading && (
        <div className="text-center my-3">
          <Spinner className='me-2' show={isLoading} />
        </div>
      )}

      {!isLoading && displayBookings && displayBookings.length > 0 ? (
        displayBookings.map((item) => (
          <div key={item.id}>
            <BookingsDetails item={item} tableLookup={tableLookup} handleChangeStatus={handleChangeStatus} loading={loading} successPage={successPage} failedPage={failedPage} enablePayment={enablePayment} enableTimer={enableTimer} paymentDoneToggle={paymentDoneToggle} />
          </div>
        ))
      ) : (
        <div className="mt-5 text-center">
          No upcoming bookings. Book your shuttle now.
        </div>
      )}
    </>
  );
};


Registry.addControl({
  id: "f65b480b-69c4-4e82-8d91-c13302fb7764",
  name: "Shuttle Payment Successful",
  type: ControlType.ShuttlePaymentSuccessful,
  properties: ShuttleBookingProperties,
  component: ShuttlePaymentSuccessfulComponent
});
